import styled from 'styled-components'

import { HeaderSpacerNoMobile } from 'components/core/header/Header'
import { ProfileHeader } from 'components/core/header/ProfileHeader'
import * as ls from 'local-storage'

import { StatusCellsContainer } from 'components/dashboard/status/StatusCells'
import DefaultLayout from 'components/core/layout/DefaultLayout'
import { useAccount, useNetwork, useProvider, useSignMessage } from 'wagmi'
import { ArtGobblers__factory, Goo__factory, Pages__factory } from 'contracts'
import { useContractAddresses } from 'hooks/gobblers/useContractAddresses'
import { useSmartContractReadCall } from 'hooks/react-query-typechain/useSmartContractReadCall'
import { DEFAULT_CHAIN_ID, DEFAULT_CHAIN_ID_NUMBER, SIWE_CONFIG } from 'config'
import { trpc } from '~/utils/trpc'
import { useMemo, useEffect } from 'react'
import {
  // device,
  // LAST_STABLE_DRAW_TOOL_VERSION,
  SAVED_DRAWINGS_ID_ARRAY_KEY,
  // ARCHIVED_SAVED_DRAWINGS_ID_ARRAY_KEY,
} from 'components/draw/constants'
import { useNftsInWalletByCollection } from 'hooks/nfts/useNftsInWallet'
import {
  CallToActionCell,
  CellTheme,
  SummonGobblerCell,
  ArtSubmissionsCard,
} from 'components/dashboard/CallToActionCell'
import { useRouter } from 'next/router'
import GooStatusCell from 'components/dashboard/status/GooStatusCell'
import ContinueDraftStatusCell from 'components/dashboard/status/ContinueDraftStatusCell'
import NewArtStatusCell from 'components/dashboard/status/NewArtStatusCell'
import { getCleanNumber } from 'components/factory/GooEquation'
import { formatUnits } from '@ethersproject/units'
import DashboardGobblersAndPages from 'components/dashboard/DashboardGobblersAndPages'
import {
  InviteArtistModal,
  InviteArtistModalState,
  useModalTransition,
} from 'components/dashboard/InviteArtistModal'
import { HideOnSmallerMobile, ShowOnSmallerMobile } from 'components/core/layout/Responsive'
import useIsMobile from 'hooks/util/useIsMobile'
import { animated, useTrail } from '@react-spring/web'
import { useGobblerAuth } from 'hooks/useGobblerAuth'
import { useConnectModal, useAccountModal, useChainModal } from '@rainbow-me/rainbowkit'
import { getCsrfToken, signIn } from 'next-auth/react'
import { SiweMessage } from 'siwe'
const FooterSpacer = styled.div`
  height: 120px;
`

const DashboardPage = () => {
  const chainId = DEFAULT_CHAIN_ID //chain?.id ?? DEFAULT_CHAIN_ID
  const provider = useProvider({
    chainId: DEFAULT_CHAIN_ID_NUMBER,
  })

  const { chain } = useNetwork()

  const accountData = useAccount()

  const sessionGobblerAuth = useGobblerAuth()

  const walletAddress = accountData.address
  const addresses = useContractAddresses(chainId)

  const artGobblersContractAddress = addresses?.ART_GOBBLERS
  const gooContractAddress = addresses?.GOO
  const pagesContractAddress = addresses?.PAGES

  const artGobblersReadContract = useMemo(() => {
    return artGobblersContractAddress
      ? ArtGobblers__factory.connect(artGobblersContractAddress, provider)
      : undefined
  }, [artGobblersContractAddress, provider])

  const gooReadContract = gooContractAddress
    ? Goo__factory.connect(gooContractAddress, provider)
    : undefined
  const pagesReadContract = pagesContractAddress
    ? Pages__factory.connect(pagesContractAddress, provider)
    : undefined

  const gobblerPriceQuery = useSmartContractReadCall(artGobblersReadContract, 'gobblerPrice')
  const gobblerPriceInFormattedUnits = formatUnits(gobblerPriceQuery.data ?? 0, 18)
  const gobblerPriceFormatted = gobblerPriceQuery.data
    ? getCleanNumber(parseFloat(gobblerPriceInFormattedUnits), 2, 2)
    : '-'

  const pagePriceQuery = useSmartContractReadCall(pagesReadContract, 'pagePrice')
  const pagePriceInFormattedUnits = formatUnits(pagePriceQuery.data ?? 0, 18)
  const pagePriceFormatted = pagePriceQuery.data
    ? getCleanNumber(parseFloat(pagePriceInFormattedUnits), 2, 2)
    : '-'

  const gooBalanceQuery = useSmartContractReadCall(gooReadContract, 'balanceOf', {
    callArgs: [walletAddress!],
    enabled: !!walletAddress,
  })

  const hasEnoughBalanceForGobbler = gobblerPriceQuery.data
    ? gooBalanceQuery.data?.gte(gobblerPriceQuery?.data ?? 0)
    : false

  const hasEnoughBalanceForPage = pagePriceQuery.data
    ? gooBalanceQuery.data?.gte(pagePriceQuery?.data ?? 0)
    : false

  const gobblerUserData = useSmartContractReadCall(artGobblersReadContract, 'getUserData', {
    callArgs: [walletAddress!],
    enabled: !!walletAddress,
  })

  const [gobblersOwnedCount, emissionMultiple, lastBalance, lastTimestamp] =
    gobblerUserData.data ?? []

  const drawingsInLocalStorage = ls.get<Array<string>>(SAVED_DRAWINGS_ID_ARRAY_KEY) || []

  const pagesOwned = useNftsInWalletByCollection(walletAddress, addresses?.PAGES, chainId)
  const numPagesOwned = pagesOwned.data?.ownedNfts.length
  const gobblersOwned = useNftsInWalletByCollection(walletAddress, addresses?.ART_GOBBLERS, chainId)
  const numGobblersOwned = gobblersOwned.data?.ownedNfts.length

  // const mostRecentDraftQuery = trpc.useQuery([
  //   'drawing.getMostRecentDraft',
  //   { privateDrawingIds: drawingsInLocalStorage ?? [] },
  // ])

  // const pagesRecentlyGlaminated = trpc.useQuery(['page.recentlyGlaminated', {
  // }])

  // console.log('pagesRecentlyGlaminated', pagesRecentlyGlaminated.data)

  const { data: unreadArtSubmissionsForUser } = trpc.useQuery(
    ['submissions.getUnreadSubmissions', { walletAddress: walletAddress! }],
    { enabled: !!walletAddress },
  )

  const router = useRouter()

  useEffect(() => {
    if (accountData.isDisconnected) {
      router.push({
        pathname: '/',
      })
    }
  }, [accountData.isDisconnected, router])

  const [modalTransitions, modalState, setModalState] = useModalTransition()

  const sectionsTrail = useTrail(5, {
    from: {
      opacity: 0,
      y: 8,
    },
    to: {
      opacity: 1,
      y: 0,
    },
  })

  const { openConnectModal } = useConnectModal()
  const { openAccountModal } = useAccountModal()
  const { openChainModal } = useChainModal()

  const { signMessageAsync } = useSignMessage()

  const handleLogin = async () => {
    try {
      const csrfToken = await getCsrfToken()

      const callbackUrl = '/dashboard'
      const message = new SiweMessage({
        domain: window.location.host,
        address: accountData?.address,
        statement: SIWE_CONFIG.statement,
        uri: window.location.origin,
        version: SIWE_CONFIG.version,
        chainId: parseInt(chainId || DEFAULT_CHAIN_ID, 10),
        nonce: csrfToken,
      })
      const signature = await signMessageAsync({
        message: message.prepareMessage(),
      })
      const res = await signIn('credentials', {
        message: JSON.stringify(message),
        redirect: false,
        signature,
        callbackUrl,
      })
    } catch (error) {
      window.alert(error)
    }
  }

  const isMobile = useIsMobile()
  return (
    <DefaultLayout>
      <HeaderSpacerNoMobile />
      <animated.div>
        <ProfileHeader />
      </animated.div>
      <AnimatedCTACellsContainer>
        <SummonGobblerCell
          cellStyle={hasEnoughBalanceForGobbler ? CellTheme.LIGHT : CellTheme.DARK}
          bottomText={`${gobblerPriceFormatted} Goo per gobbler`}
          onClick={() => {
            router.push({
              pathname: '/gobbler/mint',
            })
          }}
        />
        <CallToActionCell
          topText="Create Blank Page"
          cellStyle={hasEnoughBalanceForPage ? CellTheme.LIGHT : CellTheme.DARK}
          bottomText={`${pagePriceFormatted} Goo per page`}
          onClick={() => {
            router.push({
              pathname: '/page/mint',
            })
          }}
        />
        <CallToActionCell
          cellStyle={CellTheme.LIGHT}
          topText="Invite artist"
          bottomText="Share an invite link"
          onClick={() => {
            if (!accountData.address) {
              openConnectModal?.()
              return
            }
            if (!sessionGobblerAuth.id) {
              handleLogin()
              return
            }

            setModalState(InviteArtistModalState.open)
          }}
        />

        <ArtSubmissionsCard
          onClick={() => {
            router.push({
              pathname: '/submissions',
            })
          }}
        />
      </AnimatedCTACellsContainer>
      <StatusCellsContainer>
        <GooStatusCell />
        {!isMobile && (
          <>
            <NewArtStatusCell
            //submissions={unreadArtSubmissionsForUser?.length}
            />
            <ContinueDraftStatusCell />
          </>
        )}
      </StatusCellsContainer>
      <DashboardGobblersAndPages />
      <FooterSpacer />
      {modalTransitions((styles, item) => {
        if (item === InviteArtistModalState.closed) {
          return <div />
        }
        return (
          <InviteArtistModal
            modalState={modalState}
            setModalState={setModalState}
            styles={styles}
          />
        )
      })}
    </DefaultLayout>
  )
}

const CTACellsContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
  gap: 8px;
  margin-top: 32px;
  overflow-x: scroll;
  padding: 16px;
  margin-left: -16px;
  margin-bottom: -16px;

  ${({ theme }) => theme.mediaWidth.upToMedium`
    flex-direction: row;
    flex-wrap: wrap; 
    align-items: flex-start;
    gap: 16px;

  `}
`

const AnimatedCTACellsContainer = animated(CTACellsContainer)

export { DashboardPage }
